import Swal from "sweetalert2";
import PropTypes from 'prop-types';
import { showAlert } from "helper";
import styled from 'styled-components';
import Badge from '@/shared/components/Badge';
import Panel from "@/shared/components/Panel";
import { GeoAIFeatureTypesEnum } from "Enums";
import DeleteIcon from "mdi-react/DeleteIcon";
import CodeJsonIcon from 'mdi-react/CodeJsonIcon';
import { colorAdditional } from '@/utils/palette';
import React, { useEffect, useState } from 'react';
import GeoAIAdminInterface from '@/utils/GeoAIAdminInterface';
import { paddingLeft, paddingRight } from '@/utils/directions';
import { colorGreenHover, colorRedHover } from '@/utils/palette';
import ReactTableBase from '@/shared/components/table/ReactTableBase';

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  position: relative;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 20px;
    width: 20px;
    fill: ${colorAdditional};
    transition: all 0.3s;
  }

  svg:hover {
    fill: ${colorGreenHover};
  }

  .red-hover:hover {
    fill: ${colorRedHover};
  }
`;

const StatusFormatter = value => (
    value ? <Badge bg="success">True</Badge> : <Badge bg="secondary">False</Badge>
);

StatusFormatter.propTypes = {
    value: PropTypes.bool.isRequired,
};

const GeoAIRequestsList = () => {
    const [pageCount, setPageCount] = useState(1);
    const [totalCount, setTotalCount] = useState();
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [geoAIRequestsList, setGeoAIRequestsList] = useState({ tableHeaderData: [], tableRowsData: [] });
    const tableConfig = {
        isEditable: false,
        isSortable: true,
        isResizable: false,
        withPagination: true,
        withSearchEngine: true,
        manualPageSize: [10, 20, 30, 40],
        placeholder: 'Search...',
    };

    const openJSON = (result_json) => {
        const json = JSON.stringify(result_json, null, 2);
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
    }

    const handleDelete = (request) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to delete the request? It is not reversible!`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                GeoAIAdminInterface.instance.deleteGeoAIRequests(request.id).then(() => {
                    showAlert('success', 'Request is deleted!');
                    GetGeoAIRequestsData(pagination.pageIndex * pagination.pageSize, pagination.pageSize);
                }).catch((error) => {
                    showAlert('error', error.response?.data?.detail);
                });
            }
        });
    }

    const GetGeoAIRequestsData = (skip, limit) => {
        GeoAIAdminInterface.instance.getGeoAIRequests(skip, limit).then((response) => {
            setTotalCount(response.data['count'])
            setPageCount(Math.ceil(response.data['count'] / pagination.pageSize));
            const columns = [
                {
                    accessor: 'id',
                    Header: 'Id',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'user_id',
                    Header: 'User',
                },
                {
                    accessor: 'layer_name',
                    Header: 'Layer',
                },
                {
                    accessor: 'feature_type',
                    Header: 'Feature Type',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'created_at',
                    Header: 'Created Datetime',
                    disableGlobalFilter: true

                },
                {
                    accessor: 'result',
                    Header: 'Result',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'delete',
                    Header: 'Delete',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                }
            ];
            const data = [];
            response.data['data'].forEach(request => {
                data.push({
                    id: request.id,
                    user_id: request.user_id,
                    layer_name: request.layer_name,
                    feature_type: request.feature_type ? Object.keys(GeoAIFeatureTypesEnum).find(key => GeoAIFeatureTypesEnum[key] === request.feature_type) : '',
                    created_at: new Date(`${request.created_at}`).toLocaleString(),
                    result: <CartTableButton
                        type="button"
                        onClick={() => openJSON(request.result)}>
                        <CodeJsonIcon />
                    </CartTableButton>,
                    delete: <CartTableButton type="button" onClick={() => handleDelete(request)}><DeleteIcon className="red-hover" color='#ddd' /></CartTableButton>

                });
            });

            const geoAIRequestsListTableData = { tableHeaderData: columns, tableRowsData: data };
            setGeoAIRequestsList(geoAIRequestsListTableData);
        }).catch((error) => {
            showAlert('error', error.response?.data?.detail);
        })
    };

    useEffect(() => {
        GetGeoAIRequestsData(pagination.pageIndex * pagination.pageSize, pagination.pageSize);
    }, [pagination]);

    useEffect(() => {
        GetGeoAIRequestsData(pagination.pageIndex * pagination.pageSize, pagination.pageSize);
    }, []);

    return (
        <Panel lg={12} xl={12} md={12} title='GeoAI Requests List' refreshRequest={() => GetGeoAIRequestsData(pagination.pageIndex * pagination.pageSize, pagination.pageSize)} dismissButton={() => { return }}>
            <ReactTableBase
                columns={geoAIRequestsList.tableHeaderData}
                data={geoAIRequestsList.tableRowsData}
                tableConfig={tableConfig}
                setPagination={setPagination}
                pageCount={pageCount}
                dataLength={totalCount}
            />
        </Panel>
    );
};

GeoAIRequestsList.propTypes = {
    geoAIRequestsListTableData: PropTypes.shape({
        tableHeaderData: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string,
            name: PropTypes.string,
        })),
        tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
    }).isRequired,
};

export default GeoAIRequestsList;
